import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from '../../../shared/components/ui/Modal';
import CustomTextInput from '../../../shared/components/ui/CustomTextInput';
import AppButton from '../../../shared/components/ui/AppButton';
import { useForgotPasswordMutation } from '../../../store/api/authApi';

const ModalContent = styled.div`
  padding: 40px 30px;
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.textDark};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: 10px;
  font-weight: 600;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: 30px;
  line-height: 1.5;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

const UserTypeContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const UserTypeButton = styled.button<{ $isSelected: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : theme.colors.textMuted};
  background: ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.white : theme.colors.textDark};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: capitalize;

  &:hover {
    border-color: ${({ theme }) => theme.colors.darkPurple};
    background: ${({ theme }) => theme.colors.darkPurple};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.errorRed};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: 10px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4CAF50;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: 10px;
  text-align: center;
`;

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultUserType?: string;
}

const emailIcon = require('../../../assets/icons/email.png');

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  defaultUserType = 'student'
}) => {
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState(defaultUserType);
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [forgotPassword, { isLoading, error }] = useForgotPasswordMutation();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setEmailError('');
    setSuccessMessage('');

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      await forgotPassword({
        email: email.trim(),
        usertype: userType
      }).unwrap();

      setSuccessMessage('Password reset instructions have been sent to your email address.');
      setEmail('');
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 3000);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setEmailError(err?.data?.message || 'Failed to send reset email. Please try again.');
    }
  };

  const handleClose = () => {
    setEmail('');
    setEmailError('');
    setSuccessMessage('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <Title>Forgot Password?</Title>
        <Description>
          Enter your email address and select your account type. We'll send you instructions to reset your password.
        </Description>

        <FormContainer>
          <UserTypeContainer>
            <UserTypeButton
              type="button"
              $isSelected={userType === 'student'}
              onClick={() => setUserType('student')}
            >
              Student
            </UserTypeButton>
            <UserTypeButton
              type="button"
              $isSelected={userType === 'tutor'}
              onClick={() => setUserType('tutor')}
            >
              Tutor
            </UserTypeButton>
          </UserTypeContainer>

          <CustomTextInput
            leftIcon={emailIcon}
            color="dark"
            value={email}
            placeholder="Enter your email address"
            onChangeText={setEmail}
            error={emailError}
          />

          <AppButton
            onClick={handleSubmit}
            title={isLoading ? "Sending..." : "Send Reset Instructions"}
            disabled={isLoading}
          />
        </FormContainer>

        {error && !emailError && (
          <ErrorMessage>
            {(error as any)?.data?.message || 'Something went wrong. Please try again.'}
          </ErrorMessage>
        )}

        {successMessage && (
          <SuccessMessage>
            {successMessage}
          </SuccessMessage>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ForgotPasswordModal;
